/**
 * The Prove7 journey — the ordered vocabulary every surface shares.
 *
 * <h3>Why it lives here</h3>
 *
 * The same seven stages were already written out twice: as the agent detail
 * page's tabs (`AgentDetailPageV2.jsx:421-450`) and as the governance journey
 * funnel (`GovernanceJourney.jsx`). A third copy in Protect would mean three
 * answers to "where is this agent up to", drifting one rename at a time. The
 * names, the order and the definitions are owned here; each surface adds its
 * own icons, links and counts.
 *
 * <h3>Build · Decide · Act is not the journey</h3>
 *
 * It is the promotion gate, and it lives inside stage 05. No agent starts at
 * Build: it is registered or discovered, given an identity, bound to an intent,
 * granted access — and only then does it reach the gate. The three phases are
 * exposed as `PROMOTE_PHASES` and hang off the Promote stage alone, so a screen
 * cannot put them at the top of a registration flow by accident.
 *
 * <h3>Stage 01 has two names</h3>
 *
 * An ExteLet arrives by registration; everything else is found by a scan or
 * authored here. Same position, same meaning — "how this agent came to our
 * attention" — so it is one stage with the name resolved by archetype rather
 * than two stages that have to be kept in step.
 */

/**
 * <h3>Why the icons live here too</h3>
 *
 * They were declared in `GovernanceJourney.jsx`'s own STAGE_CHROME, which was
 * right while that funnel was the only screen drawing the journey. Protect's
 * build trail draws the same seven stages, so a second map would mean Identity
 * is a fingerprint on one screen and a number on the other, drifting one import
 * at a time. The icon is part of the shared vocabulary. The href and the gap
 * sentence stay with the funnel, because they are that screen's own material.
 */
import {
  Radar, Fingerprint, Target, Lock, ShieldCheck, Zap, ClipboardCheck,
} from 'lucide-react';

/** The promotion gate's three phases. Stage 05 only. */
export const PROMOTE_PHASES = ['Build', 'Decide', 'Act'];

export const JOURNEY_STAGES = [
  {
    id: 'discovery',
    icon: Radar,
    num: '01',
    name: 'Discovery',
    /* What stage 01 is called when the agent arrived by registration rather
       than by a scan. Same stage, same position. */
    registeredName: 'Register',
    definition: 'Surfaced from the network, code, marketplace, or shadow scan.',
  },
  {
    id: 'identity',
    icon: Fingerprint,
    num: '02',
    name: 'Identity',
    definition: 'SPIFFE/SVID issued; cryptographic name stamped.',
  },
  {
    id: 'intent',
    icon: Target,
    num: '03',
    name: 'Intent',
    definition: 'Bound to an approved Intent Profile version.',
  },
  {
    id: 'access',
    icon: Lock,
    num: '04',
    name: 'Access',
    definition: 'Role bindings resolved across Org · Tenant · Instance.',
  },
  {
    id: 'promote',
    icon: ShieldCheck,
    num: '05',
    name: 'Promote',
    gate: true,
    phases: PROMOTE_PHASES,
    definition: 'Build · Decide · Act promotion complete.',
  },
  {
    id: 'enforce',
    icon: Zap,
    num: '06',
    name: 'Enforce',
    live: true,
    definition: 'Every call passes the six-phase enforcement gate.',
  },
  {
    id: 'audit',
    icon: ClipboardCheck,
    num: '07',
    name: 'Audit',
    definition: 'Hash-chained event log; every action sealed and replayable.',
  },
];

/**
 * The stages this archetype actually walks.
 *
 * An ExteLet clears a registration gate first — until a registration row
 * exists there is no agent to mint an identity for. A native agent is authored
 * in the platform, so there is no such gate and the journey opens at Identity.
 */
export function journeyFor(archetype) {
  const extelet = String(archetype || '').toLowerCase() === 'extelet';
  if (!extelet) return JOURNEY_STAGES.slice(1);
  const [first, ...rest] = JOURNEY_STAGES;
  return [{ ...first, name: first.registeredName }, ...rest];
}

/** Where a stage sits in a journey, or -1 when that journey does not have it. */
export function stageIndex(stages, id) {
  return (Array.isArray(stages) ? stages : []).findIndex((s) => s.id === id);
}
